import { Box, Flex, IconButton, Input, Text, Textarea } from '@lego/klik-ui';
import { Edit } from '@lego/klik-ui/icons';
import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type { EditableFieldEditorProps } from '@/components/common/details/DetailsBox';
import { EditButtons } from '@/components/common/EditButtons';

type EditableFieldProps = {
  fieldKey: string;
  value: ReactNode;
  editableValue?: string;
  editedValue: string;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: (key: string, currentValue: ReactNode) => void;
  onCancel: () => void;
  onSave: (key: string, value?: string) => Promise<void>;
  onInputChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => void;
  onValueChange: (value: string) => void;
  renderEditor?: (props: EditableFieldEditorProps) => ReactNode;
};

export const EditableField = ({
  fieldKey,
  value,
  editableValue,
  editedValue,
  isEditing,
  isSaving,
  onCancel,
  onStartEdit,
  onSave,
  onInputChange,
  onKeyDown,
  onValueChange,
  renderEditor,
}: EditableFieldProps) => {
  const valueForEditing = editableValue ?? String(value ?? '');
  const isUnchanged = valueForEditing === editedValue;
  return (
    <Box>
      <Text fontWeight="medium">{fieldKey}</Text>
      {isEditing ? (
        <Flex align="start" gap={2}>
          {renderEditor ? (
            renderEditor({ value: editedValue, onChange: onValueChange })
          ) : fieldKey === 'Product ID' ? (
            <Input.Group size="sm">
              <Input.LeftAddon>PRO-</Input.LeftAddon>
              <Input
                autoFocus
                onChange={onInputChange}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => onKeyDown(e, fieldKey)}
                value={editedValue}
              />
            </Input.Group>
          ) : (
            <Textarea
              autoFocus
              onChange={onInputChange}
              onFocus={(e: FocusEvent<HTMLTextAreaElement>) => {
                const target = e.currentTarget;
                target.setSelectionRange(target.value.length, target.value.length);
              }}
              onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => onKeyDown(e, fieldKey)}
              resize="vertical"
              size="sm"
              value={editedValue}
            />
          )}
          <EditButtons
            isEditing={isEditing}
            isSaving={isSaving}
            onCancel={onCancel}
            onSave={async () => await onSave(fieldKey)}
            saveDisabled={isUnchanged}
          />
        </Flex>
      ) : (
        <Flex align="start" gap={2}>
          <Box>{value}</Box>
          <IconButton
            aria-label={`Edit ${fieldKey}`}
            color="LinkText"
            flexShrink={0}
            icon={<Edit />}
            onClick={() => onStartEdit(fieldKey, valueForEditing)}
            paddingBottom="2"
            size="xs"
            variant="solid"
          />
        </Flex>
      )}
    </Box>
  );
};
